import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { resolveMedia } from '../../context/SettingsContext';
import { useWishlist } from '../../context/WishlistContext';
import ProductQuickViewModal from './ProductQuickViewModal';

export default function ProductCard({ product }) {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const [quickViewOpen, setQuickViewOpen] = useState(false);

  const isSaved = isInWishlist(product._id);
  const variants = product.variants || [];
  const lowestVariant = variants.length ? variants.reduce((lowest, variant) => Number(variant.price) < Number(lowest.price) ? variant : lowest) : null;
  const displayPrice = Number(lowestVariant?.price ?? product.price ?? 0);
  const compareAtPrice = Number(lowestVariant?.compareAtPrice ?? product.compareAtPrice ?? 0);
  const discount = compareAtPrice > displayPrice ? Math.round(((compareAtPrice - displayPrice) / compareAtPrice) * 100) : 0;
  const imageSrc = product.images?.[0] ? resolveMedia(product.images[0]) : '/assets/ganesha_idol.jpg';

  const openProduct = () => navigate(`/product/${product.slug}`);
  const handleAction = (event) => {
    event.stopPropagation();
    if (product.productType === 'variable') return openProduct();
    addToCart(product, 1);
  };
  const handleWishlist = (event) => {
    event.stopPropagation();
    toggleWishlist(product);
  };

  return (
    <>
      <article onClick={openProduct} className="group flex min-w-0 cursor-pointer flex-col overflow-hidden rounded-md border border-[#E3DDD7] bg-white transition duration-300 hover:-translate-y-0.5 hover:border-[#C7B9AC] hover:shadow-[0_10px_30px_rgba(36,25,20,.08)]">
        <div className="relative aspect-[4/5] overflow-hidden bg-[#F3F0EC]">
          {product.isBestSeller && <span className="absolute left-2 top-2 z-10 rounded-sm bg-[#241914] px-2 py-1 text-[8px] font-bold uppercase text-white sm:text-[9px]">Best seller</span>}
          {discount > 0 && !product.isBestSeller && <span className="absolute left-2 top-2 z-10 rounded-sm bg-[#E5F2E8] px-2 py-1 text-[8px] font-bold uppercase text-[#34704A] sm:text-[9px]">Save {discount}%</span>}
          
          {/* Action Icons Stack: Wishlist + Eye Quick View (Hidden by default, reveal on cursor hover) */}
          <div className="absolute right-2 top-2 z-10 flex flex-col gap-1.5 opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 transition-all duration-300">
            <button
              type="button"
              onClick={handleWishlist}
              className={`grid h-8 w-8 place-items-center rounded-full border bg-white/95 shadow-md transition-all hover:scale-110 ${
                isSaved ? 'border-[#A84343] text-[#A84343]' : 'border-white text-[#5E514A] hover:border-[#C7B9AC] hover:text-[#A84343]'
              }`}
              title={isSaved ? 'Remove from wishlist' : 'Add to wishlist'}
              aria-label={isSaved ? 'Remove from wishlist' : 'Add to wishlist'}
            >
              <svg viewBox="0 0 24 24" className={`h-4 w-4 ${isSaved ? 'fill-current' : ''}`} fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.5 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z"/>
              </svg>
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setQuickViewOpen(true);
              }}
              className="grid h-8 w-8 place-items-center rounded-full border border-white bg-white/95 text-[#5E514A] shadow-md transition-all hover:scale-110 hover:border-[#C49A45] hover:text-[#C49A45]"
              title="Quick view details"
              aria-label="Quick view details"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
          </div>

          <img src={imageSrc} alt={product.name} className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.035]" onError={(event) => { event.currentTarget.src = '/assets/ganesha_idol.jpg'; }} />
        </div>

        <div className="flex flex-1 flex-col p-3 sm:p-4">
          <p className="truncate text-[8px] font-bold uppercase text-[#9D772D] sm:text-[9px]">{product.category?.name || 'Sandalwood craft'}</p>
          <h3 className="mt-1 min-h-[38px] font-heading text-[15px] font-semibold leading-[1.2] text-[#241914] sm:min-h-[44px] sm:text-[18px]">{product.name}</h3>
          <div className="mt-2 flex min-h-4 items-center gap-1.5 text-[9px] sm:text-[10px]">
            {product.ratingsCount > 0 ? <><span className="text-[#B98A2E]">★ {Number(product.ratingsAverage || 0).toFixed(1)}</span><span className="text-[#8B7E76]">({product.ratingsCount})</span></> : <span className="text-[#8B7E76]">New arrival</span>}
          </div>
          <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-[10px] text-[#76675E]">{product.productType === 'variable' ? 'From' : ''}</span>
            <span className="text-sm font-bold text-[#241914] sm:text-base">₹{displayPrice.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            {compareAtPrice > displayPrice && <span className="text-[9px] text-[#9B8E86] line-through sm:text-[10px]">₹{compareAtPrice.toLocaleString('en-IN')}</span>}
          </div>
          <button onClick={handleAction} className="mt-3 h-9 w-full rounded border border-[#9D772D] text-[9px] font-bold uppercase text-[#7F5D1E] transition hover:bg-[#9D772D] hover:text-white sm:text-[10px]">{product.productType === 'variable' ? 'Choose options' : 'Add to cart'}</button>
        </div>
      </article>

      {/* Quick View Details Modal Popup */}
      <ProductQuickViewModal
        product={product}
        isOpen={quickViewOpen}
        onClose={() => setQuickViewOpen(false)}
      />
    </>
  );
}

